import React, { useState, useRef } from 'react';
import type { SearchResult } from '../types';
import { searchApi } from '../services/api';
import { trackEvent } from '../services/telemetry';

interface SearchBarProps {
  /** Called with search results when a search completes, or [] when cleared. */
  onResults: (results: SearchResult[]) => void;
  /** When true, include session shadow/pending nodes in search results. */
  includePending?: boolean;
  /** Optional session scope to resolve search results against. */
  sessionId?: string | null;
  /** Number of current matches to display next to the input. */
  resultCount?: number;
}

/**
 * SearchBar — text input that searches the live graph and reports matching nodes.
 *
 * - Submit: calls searchApi.search and reports results via onResults.
 * - Clear: empties the input and calls onResults([]) to remove all highlights.
 * - Enter key triggers search; Escape clears the input.
 */
const SearchBar: React.FC<SearchBarProps> = ({
  onResults,
  includePending = false,
  sessionId,
  resultCount,
}) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSearch = async (q: string, source: 'enter' | 'button' = 'button') => {
    const trimmed = q.trim();
    if (!trimmed) {
      handleClear();
      return;
    }
    trackEvent('search_submitted', {
      source,
      queryLength: trimmed.length,
      path: window.location.pathname,
    });
    setLoading(true);
    setError(null);
    try {
      const results = await searchApi.search(trimmed, 5, includePending, sessionId);
      trackEvent('search_completed', {
        source,
        queryLength: trimmed.length,
        resultCount: results.length,
        path: window.location.pathname,
      });
      onResults(results);
    } catch (err) {
      trackEvent('search_failed', {
        source,
        queryLength: trimmed.length,
        path: window.location.pathname,
      });
      setError((err as Error).message ?? 'Search failed');
      onResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setError(null);
    onResults([]);
    if (inputRef.current) inputRef.current.style.height = 'auto';
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSearch(query, 'enter');
    } else if (e.key === 'Escape') {
      handleClear();
    }
  };

  const hasResults = typeof resultCount === 'number' && resultCount > 0;
  const hasQuery = query.trim().length > 0;

  return (
    <span className="search-bar" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <textarea
        ref={inputRef}
        className="search-bar__input"
        placeholder="Search…"
        value={query}
        rows={1}
        onChange={(e) => {
          setQuery(e.target.value);
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
        }}
        onKeyDown={handleKeyDown}
        disabled={loading}
        aria-label="Search graph nodes"
      />
      {hasQuery && (
        <button
          className="search-bar__clear"
          onClick={handleClear}
          title="Clear search"
          aria-label="Clear search"
        >
          <i className="modus-icons" aria-hidden="true">
            close
          </i>
        </button>
      )}
      <button
        className="search-bar__submit"
        onClick={() => handleSearch(query, 'button')}
        disabled={loading || !hasQuery}
        title="Search"
        aria-label="Search"
      >
        {loading ? (
          <i className="modus-icons" aria-hidden="true">
            circle_notch
          </i>
        ) : (
          <i className="modus-icons" aria-hidden="true">
            search
          </i>
        )}
      </button>
      {hasResults && (
        <span className="search-bar__count" style={{ fontSize: 12, color: '#666' }}>
          {resultCount} match{resultCount === 1 ? '' : 'es'}
        </span>
      )}
      {!hasResults &&
        typeof resultCount === 'number' &&
        resultCount === 0 &&
        hasQuery &&
        !loading && (
          <span
            className="search-bar__count search-bar__count--none"
            style={{ fontSize: 12, color: '#999' }}
          >
            No matches
          </span>
        )}
      {error && (
        <span className="search-bar__error" style={{ fontSize: 12, color: '#c00' }} title={error}>
          Search error
        </span>
      )}
    </span>
  );
};

export default SearchBar;
